import { Box, Typography } from "@mui/material";
import { useState, useEffect, useContext, useRef, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useDropzone } from "react-dropzone";
import useDisplaySnackbar from "../../utils/useDisplaySnackbar";
import ProgressSpinner from "../../components/Common/ProgressSpinner";
import BasicDatatable from "../Common/BasicDatatable";
import { getColumns, initialSort } from "./collateralsTableConfig";

import { getUserAttributes } from "../../utils/auth";
import { GlobalContext } from "../../context/GlobalContext";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import "../../styles/common.css";
import CollateralService from "../../services/collaterals/CollateralService";
import UserInteractionsService from "../../services/interactions/Interactions";
import { useLocation, useParams } from "react-router-dom";
import DocumentViewer from "../Common/DocumentViewer";
import CustomConfirmation from "../Common/CustomConfirmation";
import AiFileProcessingModal from "../Common/AiFileProcessingModal";
import { allowedListToUpload, getGroupStatusMap, isCondensedTranscript, isGroupAiInProgress } from "../../utils/utils";
import { useUploadContext } from "../../context/UploadContext";
import AddIcon from "@mui/icons-material/Add";
import { useMediaPlaybackRecovery } from "../../hooks/useMediaPlaybackRecovery";

dayjs.extend(utc);

const generateRows = (files: any, isInteractionAttendee?: boolean, spritesheetDownloadData?: any, interactionDetails?: any) => 
  files.map((item: any) => ({
    id: item.interactionAssetId,
    meeting_id: item.interactionId,
    file_name:item.assetLocation,
    asset_name: item.assetName,
    name: item.assetName?.split(".")?.[0] || item.assetName,
    c_type: item.assetType,
    url: item.assetLocation,
    creator_email: item.creatorEmail,
    created_at: item.createdAt,
    type: 'uploaded_collateral' as FileTobeDownloaded,
    asset_source: item.assetSource,
    uploaded_by: item.uploadedBy,
    isInteractionAttendee: isInteractionAttendee,
    spritesheetDownloadData: spritesheetDownloadData,
    interactionDetails: interactionDetails,
    aiAnalysisStatus: item.aiAnalysisStatus,
    interactionGroupId: item.interactionGroupId,
}));
type FileTobeDownloaded = 'uploaded_collateral' | 'attachment'
interface FileItemType {
  id: string;
  name: string;
  asset_name?: string;
  url: string;
  type: FileTobeDownloaded;
  creator_email?: string; 
  meeting_id?: string; 
  c_type?: string; 
  created_at?: string; 
  asset_source?: string;
  uploaded_by?: string;
}

const openExternalLink = (url: string | undefined, showSnackbar: (type: "success" | "error" | "info" | "warning", message: string) => void) => {
  const trimmedUrl = url?.trim();
  if (!trimmedUrl) {
    showSnackbar("error", "Link URL is missing");
    return;
  }

  try {
    new URL(trimmedUrl);
  } catch {
    showSnackbar("error", "Link URL is invalid");
    return;
  }

  window.open(trimmedUrl, "_blank", "noopener,noreferrer");
};

const Collaterals: React.FC<{
  meetingId: string;
  type: "other" | "transcript";
  isInteractionAttendee?: boolean; 
  selectedTab?: string;
  interactionDetails?: any;
  onNavigateToRecording?: (groupId?: string) => void;
}> = ({ meetingId, type, isInteractionAttendee, selectedTab, interactionDetails, onNavigateToRecording }) => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useDisplaySnackbar();
  const [savedfiles, setSavedFiles] = useState<any[]>([]);
  const [rows, setRows] = useState<any[]>([]);
  const [totalRecord, setTotalRecord] = useState(0);
  const [deleteCollateralId, setDeleteCollateralId] = useState("");
  const [downloadCollateral, setDownloadCollateral] = useState<any>("");
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [showSpinner, setShowSpinner] = useState<boolean>(false);
  const loggedInEmail = getUserAttributes()?.email;
  const { pipState, setPipState, impersonationDetails, featureMap } = useContext(GlobalContext);
  const { multipartUploadState, startUpload, cancelUpload, retryUpload } = useUploadContext();

  const aiProcessingEnabled = featureMap?.ai_analysis === true;
  const [openAiModal, setOpenAiModal] = useState<boolean>(false);
  const [selectedAiFile, setSelectedAiFile] = useState<any>(null);
  const [activeAiRowId, setActiveAiRowId] = useState<string>("");
  const [aiTriggered, setAiTriggered] = useState<boolean>(false);
  const [copyAction, isCopyAction] = useState(false);
  const [isVideoAction, setIsVideoAction] = useState(false);
  const [isOpenVideoDialog, setIsOpenVideoDialog] = useState(false);
  const [activeMediaFileId, setActiveMediaFileId] = useState<string | null>(null);
  const [videoUrl, setVideoUrl] = useState("");
  const [currentTime, setCurrentTime] = useState(0);
  const [transcript, setTranscript] = useState("");
  const [openView, setOpenView] = useState(false);
  const [transcripts, setTranscripts] = useState<any[]>([]);
  const [teamMeetingId, setTeamMeetingId] = useState<string>("");
  const videoRef = useRef<HTMLVideoElement | HTMLAudioElement | null>(null);
  const transcriptRefs = useRef<(HTMLParagraphElement | null)[]>([]);
  const [selectedFileId, setSelectedFileId] = useState<string>("");
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const is_team_meeting_id = true;
  const { videoFileId , resumeTime } = pipState;
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [isUserScrolling, setIsUserScrolling] = useState(false);
  const ScrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const selectedTabs = location.state?.selectedTab ?? params.get("st") ?? "";
  const sharedWithMe = location.state?.sharedWithMe ?? selectedTabs === "sharedWithMe"? true : false;
  const sharedByMe = location.state?.sharedByMe ?? selectedTabs === "sharedByMe"? true : false;
  const [ selectedRecordingIndex, setSelectedRecordingIndex] = useState(0);
  const [videoAssetIds, setVideoAssetIds] = useState<string[]>([]);
  const [transcriptAssetIds, setTranscriptAssetIds] = useState<string[]>([]);

  const dropzoneAccept: Record<string, string[]> = {
    "application/pdf": [".pdf"],
    "application/msword": [".doc"],
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
    "application/rtf": [".rtf"],
    "text/plain": [".txt"],
    "text/csv": [".csv"],
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
    "image/jpeg": [".jpg", ".jpeg"],
    "image/png": [".png"],
    "audio/mpeg": [".mp3"],
    "video/mp4": [".mp4"],
    "audio/mp4": [".m4a"],
    "audio/wav": [".wav"],
    "audio/x-wav": [".wav"],
  };

  const { getRootProps, getInputProps, open } = useDropzone({
    noClick: true,
    noKeyboard: true,
    multiple: false,
    accept: dropzoneAccept,
    onDrop: (acceptedFiles) => {
      if (!acceptedFiles?.length) return;

      acceptedFiles.forEach((file) => {
        const hasExtension = file.name.includes(".") && file.name.lastIndexOf(".") > 0;
        const fileExtension = hasExtension ? file.name.split(".").pop()?.toLowerCase() || "" : "";
        if (!hasExtension || !allowedListToUpload.includes(fileExtension)) {
          const msg = fileExtension
            ? `The file type '${fileExtension}' is not allowed for upload`
            : "This file type is not allowed for upload";
          showSnackbar("error", msg);
          return;
        }
        if (file.size === 0) {
          showSnackbar("error", "Cannot upload empty file.");
          return;
        }
        startUpload(file, meetingId);
      });
    },
  });

  const selectedVideoId = videoAssetIds[selectedRecordingIndex];
  const selectedTranscriptId = transcriptAssetIds[selectedRecordingIndex];
  const { id: interactionId } = useParams();
  const [downloadApiResponse, setDownloadApiResponse] = useState<any>([]);
  const [pendingDownloadFile, setPendingDownloadFile] = useState<FileItemType | null>(null);
  const [downloadApiCall, setDownloadApiCall] = useState<boolean>(false);
  const [videoPlay, setVideoPlay] = useState<boolean>(false);
  const [pendingDownloadMode, setPendingDownloadMode] = useState<"download" | "preview">("download");

  const [documentViewerOpen, setDocumentViewerOpen] = useState(false);

  const [currentDocument, setCurrentDocument] = useState({
    url: "",
    name: "",
    type: ""
  });

  const isImpersonating = impersonationDetails?.isImpersonating === true;
  const impersonatingUserPorId = impersonationDetails?.impersonatingUserPorId;
  const accessLevel = impersonationDetails?.accessType;

  const {
    isLoading: isCollateralListLoading,
    isFetching: isCollateralListFetching,
    isSuccess: isCollateralListSuccess,
    isError: isCollateralListError,
    error: collateralListError,
    data: MeetingCollateralData,
    refetch,
  } = CollateralService.useGetInteractionCollateral(
    {
      interactionId: meetingId,
      impersonatingPorId: impersonatingUserPorId,
      accessLevel: accessLevel,
      // c_types: type,
    },
    meetingId ? true : false
  );

  useEffect(() => {
    if (!meetingId) return;
    const allAssets = Array.isArray(MeetingCollateralData?.data?.collaterals) ? MeetingCollateralData?.data?.collaterals : [];
    const allStatuses = allAssets.map((item: any) => item?.aiAnalysisStatus);
    const isAiProcessingInProgress = isGroupAiInProgress(allStatuses, aiTriggered);

    if (!isAiProcessingInProgress) {
      if (aiTriggered) setAiTriggered(false);
      return;
    }

    const interval = setInterval(() => {
      refetch();
    }, 30000); // every 30 seconds

    return () => clearInterval(interval);
  }, [meetingId, refetch, MeetingCollateralData, aiTriggered]);

  const activeVideoAssetId =
    isOpenVideoDialog && (downloadCollateral?.id || activeMediaFileId)
      ? downloadCollateral?.id || activeMediaFileId
      : selectedTab === "assets-recordings"
      ? selectedVideoId
      : downloadCollateral?.id || selectedVideoId;

  const {
    isLoading: isDownloadLoading,
    isFetching: isDownloadFetching,
    isSuccess: isDownloadSuccess,
    isError: isDownloadError,
    error: downloadError,
    data: downloadData,
    refetch: refetchVideoUrl,
  } = CollateralService.useGetAssetDownloadUrl(
    {
      assetId: activeVideoAssetId,
      isTeamInteraction: is_team_meeting_id,
      sharedWithMe: sharedWithMe || undefined,
      interactionId: meetingId,
      sharedByMe: sharedByMe || undefined,
      impersonatingPorId: impersonatingUserPorId,
      accessLevel: accessLevel,
    },
     (!!selectedVideoId && selectedTab === "assets-recordings") ||
      videoPlay ||
      (!!activeVideoAssetId && isOpenVideoDialog && isVideoAction),
  );

  const collateralsList = Array.isArray(MeetingCollateralData?.data?.collaterals) ? MeetingCollateralData?.data?.collaterals : [];
  const condensedTranscript = collateralsList.find(
    (item: any) =>
      item.assetType === "transcript" &&
      isCondensedTranscript(item.assetLocation)
  );

  const spritesheetAsset = useMemo(() => {
    const list = Array.isArray(MeetingCollateralData?.data?.collaterals) ? MeetingCollateralData?.data?.collaterals : [];
    return (
      list.find(
        (item: any) => item.assetType === "spritesheet" && item.assetName
      ) || null
    );
  }, [MeetingCollateralData]);
  
  const { 
    data: spritesheetDownloadData,
    refetch: refetchSpritesheetUrl,
  } = CollateralService.useGetAssetDownloadUrl(
    {
      assetId: spritesheetAsset?.interactionAssetId,
      isTeamInteraction: is_team_meeting_id,
      sharedWithMe: sharedWithMe || undefined,
      ...(sharedWithMe === true && { interactionId: interactionId || undefined }),
      sharedByMe: sharedByMe || undefined,
      interactionId: meetingId,
    },
    !!spritesheetAsset?.interactionAssetId && ((!!selectedVideoId && selectedTab === "assets-recordings") || videoPlay)
  );

  const {
    resetRecoveryState,
    mediaHandlers,
  } = useMediaPlaybackRecovery({
    mediaRef: videoRef,
    currentUrl: videoUrl,
    onUrlUpdate: setVideoUrl,
    refetchMediaUrl: refetchVideoUrl,
    refetchSpritesheetUrl,
    hasSpritesheet: !!spritesheetAsset?.interactionAssetId,
    isActive: isOpenVideoDialog && !!activeVideoAssetId,
  });
const {
  isLoading: isDownloadApiLoading,
  isFetching: isDownloadApiFetching,
  isSuccess: isDownloadApiSuccess,
  isError: isDownloadApiError,
  error: downloadApiError,
  data: downloadApiData,
} = CollateralService.useDownloadCollateral(
  {
    assetId: downloadCollateral?.id || selectedVideoId,
    isTeamInteraction: is_team_meeting_id,
    sharedWithMe: sharedWithMe || undefined,
    ...(sharedWithMe === true && { interactionId: meetingId || undefined }),
    sharedByMe: sharedByMe || undefined,
    interactionId: meetingId,
  },
   !!downloadCollateral?.id && downloadApiCall && !isOpenVideoDialog,
);

useEffect(() => {
  setShowSpinner(isDownloadApiLoading || isDownloadApiFetching)
  if (isDownloadApiSuccess && downloadApiData) {
    const downloadData = downloadApiData?.data;
    if (downloadData instanceof Blob) {
      setDownloadApiResponse(downloadData);
    } else {
      const blob = new Blob([downloadData], { type: "application/pdf" });
      setDownloadApiResponse(blob);
    }
  } if(isDownloadApiError) {
    showSnackbar("error", downloadApiError?.message);
  }
}, [isDownloadApiSuccess, downloadApiData, isDownloadApiLoading, isDownloadApiFetching, downloadApiError]);


  const {
    isLoading: isAdminLoading,
    isFetching: isAdminFetching,
    isSuccess : isAdminSucess,
    isError: isAdminError,
    error: isAdminDataError,
    data: meetingsData,
  } = CollateralService.UseGetTranscriptView(
    {
      interactionId: interactionId || teamMeetingId,
      assetId: selectedFileId || condensedTranscript?.interactionAssetId,
    },
      Boolean(
        (selectedFileId || condensedTranscript?.interactionAssetId) &&
        (openView || (type === "transcript" && !!selectedTranscriptId))
      ),
  );

  useEffect(()=>{ 
    if(isOpenVideoDialog) {
      setVideoPlay(false);
    }
  }, [isOpenVideoDialog])
  useEffect(() => {
    if (isAdminSucess && meetingsData) {
      const transcriptData =
        meetingsData?.data?.transcript ||
        (typeof meetingsData?.data === "string" ? meetingsData.data : "");
      const errorMessage = meetingsData?.data?.errorMessage;
      if (errorMessage) {
        setTranscript("");
      }
      else if (transcriptData) {
        setTranscript(transcriptData);
      }
      else {
        setTranscript("");
      }
    }
  }, [isAdminSucess, isAdminLoading, isAdminFetching, isAdminError, isAdminDataError, meetingsData]);

  const { mutate: mutateAssetAnalyze, isPending: isAiProcessingSubmitting } =
    UserInteractionsService.usePostAssetAnalyze();

  useEffect(() => {
    if(transcript){
      setTranscripts(parseTranscript(transcript));
    } else {
      setTranscripts([]);
    }
  },[transcript]);
  
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
  
    const handleEnterPiP = () => {
      setPipState(prev => ({
        ...prev,
        videoElement: videoRef.current as HTMLVideoElement,
        meetingId: meetingId,
        triggered: true,
      }));
      setIsOpenVideoDialog(false);
    };
    
    video.addEventListener("enterpictureinpicture", handleEnterPiP);
    return () => {
      video.removeEventListener("enterpictureinpicture", handleEnterPiP);
    };
  }, [videoRef.current]);
  
  useEffect(() => {
    if (!videoFileId || rows.length === 0) return;
  
    const fileExists = rows.some((file) => file.id === videoFileId);
    if (!fileExists) return;
    if (!isOpenVideoDialog) {
      handleOpenVideo(videoFileId);
      setPipState(prev => ({ ...prev, videoFileId: null }));
    }
  }, [videoFileId, rows, isOpenVideoDialog]);
  
  const convertToSeconds = (time: string): number => {
    const [hh, mm, ss] = time.replace(",",".").split(":");
    return parseInt(hh) * 3600 + parseInt(mm) * 60 + Math.floor(parseFloat(ss));
  };

  const parseTranscript = (rawText: any) => {
    if (!rawText || typeof rawText !== "string") return [];
    const lines = rawText.replace(/\r/g, "").trim().split("\n");
    const videoStartTime = meetingsData?.data?.videoStartTime;
    const isWebVttFormat = lines.some((line: string) => line.trim().toUpperCase().startsWith("WEBVTT") );
      if (isWebVttFormat) {
        return parseWebVttFormat(lines);
      } else {
        return parseTimestampedFormat(lines, videoStartTime);
      }
  };
    const parseWebVttFormat = (lines: any) => {
      const result: any[] = [];
      for (let i = 0; i < lines.length; i++) {
          const match = lines[i].match(/(\d{2}:\d{2}:\d{2}[.,]\d{3}) --> (\d{2}:\d{2}:\d{2}[.,]\d{3})/);
          if (match && i + 1 < lines.length) {
            const startTime = convertToSeconds(match[1]);
            const endTime = convertToSeconds(match[2]);
            const speakerTextMatch = lines[i + 1].match(/^([^:]+):\s*(.*)$/);
            if (speakerTextMatch) {
              const speaker = speakerTextMatch[1].trim();
              const text = speakerTextMatch[2].trim();
              i++;
              while (i < lines.length && lines[i].trim() === "") {
                i++;
              }
              result.push({
                speaker,
                start: startTime,
                end: endTime,
                text,
              });
            }
          }
      }
      return result;
    };
      const parseTimestampedFormat = (lines: any, videoStartTime: string) => {
        const referenceTime = videoStartTime ? dayjs.utc(videoStartTime) :(()=> {
        const timestamps = lines.map((line:any) => {
          const match = line.match(/^(.+?),(.+?),(.+?) => (.*)$/);
          return match ? dayjs.utc(match[2].trim()) : null;
        }).
        filter(Boolean);
        return timestamps.length
        ? timestamps.reduce((minTime:any, current:any) => 
          (current.isBefore(minTime) ? current : minTime)): dayjs.utc();
      })();
          return lines
            .map((line:any)=>{
              const match = line.match(/^(.+?),(.+?),(.+?) => (.*)$/);
              if (!match) return null;
              const [, speaker, startTimeRaw, endTimeRaw, text] = match;
              const start = dayjs.utc(startTimeRaw.trim());
              const end = dayjs.utc(endTimeRaw.trim());
              if (!start.isValid() || !end.isValid()) return null;
          return {
            speaker: speaker.replace(/\s*\[.*?\]/g, ''),
            start: start.diff(referenceTime, "second"),
            end: end.diff(referenceTime, "second"),
            text: text,
          };
        })
        .filter(Boolean);
      };

  useEffect(() => {
      const interval = setInterval(() => {
      if (!videoRef.current) return;
        setCurrentTime(videoRef.current.currentTime);
    }, 500);
    return () => clearInterval(interval);
  }, [videoRef]);

  useEffect(() => {
    const container = containerRef.current;
    const handleScroll = () => {
      setIsUserScrolling(true);
      if (ScrollTimeoutRef.current) {
        clearTimeout(ScrollTimeoutRef.current);
      }
      ScrollTimeoutRef.current = setTimeout(() => {
        setIsUserScrolling(false);
      },5000);
    }
    if (container) {
      container.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (container) {
        container.removeEventListener("scroll", handleScroll);
      }
      if (ScrollTimeoutRef.current)
        clearTimeout(ScrollTimeoutRef.current);
    };
  },[containerRef.current]);

  useEffect(() => {
    const video = videoRef.current;
    if (isUserScrolling || !video || video.paused) return;
    const activeIndex = transcripts.findIndex(
      (t:any) => currentTime >= t.start && currentTime <= t.end
    );
    if (activeIndex !== -1 && transcriptRefs.current[activeIndex]) {
      transcriptRefs?.current[activeIndex]?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [currentTime,isUserScrolling]);
  
  // Upload orchestration moved to central UploadContextProvider

  const { mutate: mutateMeetingCollateralDelete } =
    CollateralService.useDeleteInteractionCollateral({
      onMutate: () => {
        setShowSpinner(true);
      },

      
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["get_interaction_collateral"],
        });
        showSnackbar("success", "Interaction Collateral deleted successfully");
      },
      onError: (error: string) => {
        showSnackbar("error", `${error}`);
      },
      onSettled: () => {
        setShowSpinner(false);
        handleConfirmClose();
      },
    });

    useEffect(() => {
      if (isCollateralListSuccess) {
        const resultList = Array.isArray(MeetingCollateralData?.data?.collaterals) ? MeetingCollateralData?.data?.collaterals : [];
        const result = resultList.filter(
          (item: any) =>
            item.assetName &&
            item.assetLocation
        );
    
    const filteredResult =
      type === "transcript"
        ? result.filter(
            (item: any) =>
              (item.assetType === "transcript" &&
                (item.assetLocation?.endsWith(".mp4") || item.assetLocation?.endsWith(".m4a"))) ||
              item.assetType === "audio" || 
              (item.assetType === "transcript" && isCondensedTranscript(item.assetLocation))
          )
        : result.filter((item: any) => item.assetType !== "transcript" && item.assetType !== "audio");
      
      setSavedFiles(filteredResult);
      setTotalRecord(filteredResult.length);
        // Group mp4 transcript videos
        const videos = result
          .filter(
            (item: any) =>
              item.assetType === "transcript" &&
              item.assetLocation?.endsWith(".mp4")
          )
          .map((item: any) => item.interactionAssetId);
    
        // Group text transcripts
        const transcripts = result
          .filter(
            (item: any) =>
              item.assetType === "transcript" &&
              item.assetLocation?.toLowerCase().endsWith(".txt") &&
              isCondensedTranscript(item.assetLocation)
          )
          .map((item: any) => item.interactionAssetId);
    
        setVideoAssetIds(videos);
        setTranscriptAssetIds(transcripts);
    
      }
    
      if (isCollateralListError) {
        const errorMessage =
          collateralListError?.response?.data?.errors ||
          collateralListError?.message ||
          "An unknown error occurred";
        showSnackbar("error", errorMessage);
      }
  }, [
    isCollateralListLoading,
    isCollateralListFetching,
    isCollateralListSuccess,
    collateralListError,
    MeetingCollateralData,
  ]);

  useEffect(() => {
    if (isDownloadSuccess) {
      const fileData = downloadData?.data?.downloadUrl; 
      if (copyAction) {
        // Skip download logic during a share action
        isCopyAction(false);
        setDownloadCollateral("");
        navigator.clipboard
            .writeText(fileData)
            .then(() => {
              showSnackbar("success", "Download link copied to clipboard!");
            })
            .catch(() => {
              showSnackbar("error", "Failed to copy the download link.");
            });

        return;
      }
      if (isVideoAction || isOpenVideoDialog) {
        setVideoUrl(fileData);
        return;
      }
      if (fileData) {
        //showSnackbar("success", "Downloading started");
        // window.open(fileData, "_blank", "noreferrer");
      }
  
      setDownloadCollateral("");
      setIsVideoAction(false);
    }

    if (isDownloadError) {
      showSnackbar("error", downloadError);
    }
  }, [
    isDownloadLoading,
    isDownloadFetching,
    isDownloadSuccess,
    isDownloadError,
    downloadData,
    isOpenVideoDialog,
    isVideoAction,
  ]);

  useEffect(() => {
    if (isOpenVideoDialog && downloadData?.data?.downloadUrl) {
      setVideoUrl(downloadData.data.downloadUrl);
    }
  }, [isOpenVideoDialog, activeMediaFileId, downloadData]);


  useEffect(() => {
    const filteredSavedFiles =
    savedfiles?.filter((file: any) => {
      const name = (file.assetLocation || "").toLowerCase();
      return (
        !name.endsWith('.txt') &&
        !name.endsWith('spritesheet.png') ||
        isCondensedTranscript(name)
      );
    }) ?? [];
  
    const uploadedData = generateRows(filteredSavedFiles, isInteractionAttendee, spritesheetDownloadData, interactionDetails);
    const uploadingRows = multipartUploadState
      .filter((upload) => upload.interactionId === meetingId)
      .map((upload) => ({
        id: upload.uploadId,
        asset_name: upload.fileName,
        name: upload.fileName,
        file_name: upload.fileName,
        uploaded_by: loggedInEmail,
        asset_source: "Account",
        creator_email: loggedInEmail,
        progress: upload.progress,
        isUploading: ["starting", "uploading", "completing"].includes(upload.status),
        isError: upload.status === "failed",
        errorMessage: upload.errorMessage,
        uploadState: upload,
        created_at: new Date().toISOString(),
      }));
  
    const combined = [...uploadedData, ...uploadingRows];
  
    const uniqueRows = Array.from(
      new Map(combined.map(item => [item.id, item])).values()
    );

    const sortedRows = uniqueRows.sort((a, b) => {
      const getTimestamp = (item: any) => {
        // primary source
        if (item?.created_at) return new Date(item.created_at).getTime();

        // fallback → parse from filename if needed
        const fileName = item?.file_name || item?.name || "";
        const matchDate = fileName.match(/(\d{4}-\d{2}-\d{2})/);
        const matchTime = fileName.match(/T(\d{2}-\d{2}-\d{2})Z/);

        if (matchDate && matchTime) {
          const date = matchDate[1];
          const time = matchTime[1].replace(/-/g, ":");
          return new Date(`${date}T${time}Z`).getTime();
        }

        return 0;
      };

      return getTimestamp(b) - getTimestamp(a); // 🔥 newest first
    });

    setRows(sortedRows);
  }, [savedfiles, multipartUploadState]);

  const handleDownload = (fileId: string) => {
    const fileTobeDownloaded: FileItemType | undefined = rows.find(
      (file: FileItemType) => file.id === fileId
    );
    if (!fileTobeDownloaded) return;

    const isLink =
      fileTobeDownloaded.c_type?.toLowerCase() === "link" ||
      (fileTobeDownloaded as any).assetType?.toLowerCase() === "link";

    if (isLink) {
      openExternalLink(fileTobeDownloaded.url, showSnackbar);
      return;
    }
  
    if (fileTobeDownloaded.type === "uploaded_collateral") {
      setDownloadApiCall(true)
      setDownloadCollateral(fileTobeDownloaded);   // triggers API fetch
      setPendingDownloadFile(fileTobeDownloaded);  // remember which file to download
      setPendingDownloadMode("download");
    }
  };

  const handlePreview = (fileId: string) => {
    const fileTobeDownloaded: FileItemType | undefined = rows.find(
      (file: FileItemType) => file.id === fileId
    );
  
    if (!fileTobeDownloaded) return;

    const isLink =
      fileTobeDownloaded.c_type?.toLowerCase() === "link" ||
      (fileTobeDownloaded as any).assetType?.toLowerCase() === "link";

    if (isLink) {
      openExternalLink(fileTobeDownloaded.url, showSnackbar);
      return;
    }
  
    if (fileTobeDownloaded.type === "uploaded_collateral") {
      setDownloadApiCall(true)
      setDownloadCollateral(fileTobeDownloaded);   // triggers API fetch
      setPendingDownloadFile(fileTobeDownloaded);  // remember which file to download
      setPendingDownloadMode("preview")
    }
  };
  
  // Effect runs when API response changes
  useEffect(() => {
    if (!pendingDownloadFile || !(downloadApiResponse instanceof Blob)) return;

    const blob = downloadApiResponse;
    const blobUrl = URL.createObjectURL(blob);

    // Use mode to differentiate between download and preview
    if (pendingDownloadMode === "preview") {
      // Open in DocumentViewer if preview
      const mime = blob.type;
      const extension = mime.split("/")[1] || "pdf";

      setCurrentDocument({
        url: blobUrl,
        name: pendingDownloadFile.name || "document",
        type: extension,
      });
      setDocumentViewerOpen(true);
    } else {
      // Fallback download
  
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = pendingDownloadFile.name || "file";
      document.body.appendChild(link);
      link.click();
      link.remove();
    }
    // Reset state after handling
    setPendingDownloadFile(null);
    setDownloadApiResponse(undefined);
    setDownloadCollateral("");
  }, [downloadApiResponse, pendingDownloadFile, pendingDownloadMode]);

  const handleSavedDelete = () => {
    mutateMeetingCollateralDelete({
      assetId: deleteCollateralId,
      interactionId: meetingId,
    });
  };

  const handleConfirmClose = () => {
    setOpenConfirmDialog(false);
    setDeleteCollateralId("");
  };

  const handleOpenVideo = (fileId: string) => {
    if (pipState.triggered && pipState.videoFileId !== fileId) {
    showSnackbar("error","Please close PiP before playing a new video");
    return;
  }
    setVideoUrl("");
    setIsVideoAction(true);
    setIsOpenVideoDialog(true);
    setActiveMediaFileId(fileId);
    setVideoPlay(true);
    setPipState(prev => ({
      ...prev,
      tempVideoFileId: fileId,
    }));
    let videoFile: FileItemType;
    if (fileId) {
      videoFile = rows.find((file: any) => file.id === fileId);
      if (videoFile) {
        setDownloadCollateral(videoFile);
      }
    }
    setSelectedFileId("");
    setTranscript("");
  };
  const handleCloseVideoDialog = () => {
    const video = videoRef.current;
    const isInPiP = document.pictureInPictureElement === video;
    if (isInPiP) {
      setPipState(prev => ({ ...prev, triggered: true ,isTeamMeeting: is_team_meeting_id}));
    }
    setIsOpenVideoDialog(false);
    setActiveMediaFileId(null);
    setIsVideoAction(false);
    setVideoUrl("");
    setDownloadCollateral("");
    resetRecoveryState();
    if (!isInPiP && video) {
      video.pause();
      video.removeAttribute("src");
      video.load();
    }
    if (resumeTime) {
      setPipState(prev => ({ ...prev, triggered: false, resumeTime: null }));
    }
  };
  const handleOpenView=(fileId:string)=>{
    setOpenView(true);
    setTranscript("");
    setTranscripts([]);
    setSelectedFileId(fileId);
    if (fileId){
      const transcript = rows.find((file: any) => file.id === fileId);
      if(transcript){
        setTeamMeetingId(transcript?.meeting_id)
      }
    }
  };

  const handleCloseView=()=>{
    setOpenView(false);
  };


  const groupTranscripts: Record<string, any> = {};
  transcripts.forEach((item) => {
    if (!groupTranscripts[item.speaker]) groupTranscripts[item.speaker] = [];
    groupTranscripts[item.speaker].push(item);
  });

  useEffect(() => {
    if(selectedTab === "assets-recordings")
    {
      setIsVideoAction(true);
    }
  if (savedfiles?.length === 1) {
    setSelectedRecordingIndex(0);
    setDownloadCollateral("");
    setIsVideoAction(true);
    setTranscripts(parseTranscript(transcript));
    setSelectedFileId(savedfiles[0].id);
  }
}, [savedfiles, selectedTab]);
  const handleOpenAiProcessing = (row: any) => {
    const rowId = row?.id || row?.original?.id;
    setSelectedAiFile(row?.original || row);
    setActiveAiRowId(rowId);
    setOpenAiModal(true);
  };

  const handleCloseAiModal = () => {
    setOpenAiModal(false);
    setActiveAiRowId("");
    setSelectedAiFile(null);
  };

  const handleProcessAiFile = (selectedOptions: string[]) => {
    const assetId = selectedAiFile?.id || selectedAiFile?.interactionAssetId || selectedAiFile?.assetId || "";
    const nameOfFile = selectedAiFile?.asset_name || selectedAiFile?.file_name || selectedAiFile?.name || "file";

    if (!assetId) {
      showSnackbar("error", "Unable to identify file asset ID for AI processing.");
      return;
    }

    mutateAssetAnalyze(
      {
        assetId,
        transcriptAnalysis: selectedOptions.includes("transcript"),
        slideAnalysis: selectedOptions.includes("slide"),
      },
      {
        onSuccess: (response: any) => {
          showSnackbar("success", response?.data?.message || `AI Processing initiated for ${nameOfFile}`);
          setAiTriggered(true);
          handleCloseAiModal();
          queryClient.invalidateQueries({ queryKey: ["get_interaction_collateral"] });
        },
        onError: (error: any) => {
          const errMsg =
            error?.response?.data?.message || "Failed to initiate AI processing";
          showSnackbar("error", errMsg);
        },
      }
    );
  };

  const groupStatusMap = useMemo(() => {
    const rawData = Array.isArray(MeetingCollateralData?.data?.collaterals) ? MeetingCollateralData?.data?.collaterals : [];
    return getGroupStatusMap(rawData);
  }, [MeetingCollateralData]);

  const columns = getColumns({
    handleDownload,
    handleOpenVideo,
    handleCloseVideoDialog,
    handleOpenView,
    handleCloseView,
    setDeleteCollateralId,
    setOpenConfirmDialog,
    handlePreview,
    handleOpenAiProcessing,
    onNavigateToRecording,
    loggedInEmail,
    openView,
    isOpenVideoDialog,
    activeMediaFileId,
    videoUrl,
    videoRef,
    transcripts,
    interactionId: meetingId,
    handleRetry: (uploadState: any) => retryUpload(uploadState.uploadId),
    cancelUpload,
    groupStatusMap,
    aiProcessingEnabled,
    mediaHandlers,
    spritesheetDownloadData
  });

  return (
    <div style={{ width: "100%", overflow: "auto", padding: "0px", paddingRight: "20px" }}>
    <>
    <div style={{ display: "none" }}>
        <div {...getRootProps()}>
          <input {...getInputProps({ accept: allowedListToUpload.map((ext) => `.${ext}`).join(",") })} />
        </div>
      </div>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mt: 2,
          paddingLeft: "20px",
          paddingRight: "20px",
          mb: 1,
        }}
      >
        <Typography variant="h2" className="notes-title">
          Files and Assets that have been attached
        </Typography>
        <Box display="flex" alignItems="center" gap={2} margin={1} >
          {!sharedWithMe && isInteractionAttendee && !isImpersonating &&(
            <Box className="attach-files" onClick={open}>
              <AddIcon sx={{ fontSize: 18, color: "#6036D6" }} />
              <Typography className="attach-files-text">
                Attach Files
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
          <Box
            sx={{
              "& .MuiTableBody-root .MuiTableRow-root": { cursor: "pointer" },
              "& .MuiTableHead-root .MuiTableCell-head": {
                backgroundColor: "#EDECFC80",
                fontWeight: 600,
                fontSize: "13px",
                color: "#4B5563",
              },
            }}
          >
            <BasicDatatable
              columns={columns}
              rows={rows}
              rowCount={totalRecord}
              initialSort={initialSort}
              hidePaginationFooter={true}
              minHeight="auto"
              maxHeight="calc(100vh - 240px)"
              height= "calc(50vh + 200px)"
              isBordervisible
              columnVisibility={{
                downloadAction: sharedWithMe || !isInteractionAttendee ? false : true,
              }}
              muiTableBodyRowProps={({ row }: any) => {
                const isSelected = openAiModal && (row.id === activeAiRowId || row.original?.id === activeAiRowId);
                return {
                  sx: {
                    backgroundColor: isSelected ? "#F8E6FA !important" : "inherit",
                    "&:hover": {
                      backgroundColor: isSelected ? "#F8E6FA !important" : undefined,
                    },
                  },
                };
              }}
            />
          </Box>
      <CustomConfirmation
        title="Are you sure you want to delete this Collateral?"
        width="350px"
        actionBtnLabel="Delete"
        cancelBtnLabel="Cancel"
        isOpen={openConfirmDialog}
        handleSuccess={handleSavedDelete}
        handleClose={handleConfirmClose}
        backgroundcolor = {false}
      />
      <DocumentViewer
        open={documentViewerOpen}
        onClose={() => setDocumentViewerOpen(false)}
        fileUrl={currentDocument.url}
        fileName={currentDocument.name}
        fileType={currentDocument.type}
      />
      <AiFileProcessingModal
        open={openAiModal}
        onClose={handleCloseAiModal}
        file={selectedAiFile}
        onProcess={handleProcessAiFile}
        isSubmitting={isAiProcessingSubmitting}
      />
    </>
  <ProgressSpinner showSpinner={showSpinner} />
</div>
  );
};

export default Collaterals;
